import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormMultiSelect } from '@ipfabric/ui-components/FormMultiSelect/FormMultiSelect';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { type RoleGetResponseBodyV2, VALID_SLUG_REGEX } from '@ipfabric/utils';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import type { ReactNode } from 'react';
import { useNotifications } from 'utils/hooks/useNotifications';
import { renderQueries } from 'utils/react-query/renderQuery';
import { z } from 'zod';
import { useDataPoliciesQuery } from '../DataPolicies/dataPoliciesApi';
import { useEndpointPoliciesQuery } from '../EndpointPolicies/endpointPoliciesApi';
import { type PolicyFormMode, canSaveInMode } from '../shared/policyFormMode';
import { useWriteScopes } from '../shared/useWriteScopes';
import { useRoleQuery, useRolesV2Api } from './rolesV2Api';

export type RoleFormMode = PolicyFormMode;

const roleFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Role name is required.')
    .max(64)
    .regex(VALID_SLUG_REGEX, 'Use letters, digits, "-" and "_" only.'),
  description: z.string(),
  endpointPolicyIds: z.array(z.string()),
  dataPolicyIds: z.array(z.string()),
});

type RoleFormValues = z.infer<typeof roleFormSchema>;

const TITLE_BY_MODE: Record<RoleFormMode, string> = {
  add: 'New role',
  edit: 'Edit role',
  duplicate: 'New role',
  view: 'Role',
};

type RoleFormPanelProps = {
  mode: RoleFormMode;
  roleId?: string;
  onClose: () => void;
};

export const RoleFormPanel = ({
  mode,
  roleId,
  onClose,
}: RoleFormPanelProps) => {
  const roleQuery = useRoleQuery(roleId ?? null);
  const endpointPoliciesQuery = useEndpointPoliciesQuery();
  const dataPoliciesQuery = useDataPoliciesQuery();

  if (roleId === undefined) {
    return <RoleFormContent mode={mode} onClose={onClose} />;
  }
  // Loading and error states keep the panel frame so the user can close it
  // (closing resets the opened role, so re-opening retries the request).
  const frame = (body: ReactNode) => (
    <SidePanel
      title={TITLE_BY_MODE[mode]}
      closeOnBackdropClick={false}
      onClose={onClose}
      footer={
        <Button
          className='w-full'
          dataTestId='role-form-close'
          onClick={onClose}
          type='button'
          variant='outline'
        >
          Close
        </Button>
      }
    >
      {body}
    </SidePanel>
  );
  // The policy lists name the role's linked policies; without them the chips
  // would render before they have a label.
  return renderQueries({
    queries: {
      role: roleQuery,
      endpointPolicies: endpointPoliciesQuery,
      dataPolicies: dataPoliciesQuery,
    },
    renderStaleDataOnMount: false,
    pending: () => frame(<LoaderAndText />),
    error: () =>
      frame(<LoaderAndText hasError errorText='Failed to load the role' />),
    success: ({ role }) => (
      <RoleFormContent mode={mode} role={role} onClose={onClose} />
    ),
  });
};

type RoleFormContentProps = {
  mode: RoleFormMode;
  role?: RoleGetResponseBodyV2;
  onClose: () => void;
};

const RoleFormContent = ({ mode, role, onClose }: RoleFormContentProps) => {
  const scopes = useWriteScopes('/roles', '/roles/:roleKey');
  // Without the write scope for this mode the panel only shows the record.
  const readOnly = !canSaveInMode(mode, scopes);
  const { showNotification } = useNotifications();
  const { createRole, updateRole } = useRolesV2Api();
  const endpointPoliciesQuery = useEndpointPoliciesQuery();
  const dataPoliciesQuery = useDataPoliciesQuery();

  const form = useCustomForm<RoleFormValues>({
    defaultValues: {
      name:
        mode === 'duplicate' && role ? `${role.name}-copy` : (role?.name ?? ''),
      description: role?.description ?? '',
      endpointPolicyIds: role?.endpointPolicyIds ?? [],
      dataPolicyIds: role?.dataPolicyIds ?? [],
    },
    resolver: zodResolver(roleFormSchema),
  });
  const { control, formState, handleSubmit } = form;

  const onSubmit = handleSubmit(async (values) => {
    // Legacy policies are not editable here but still enforce access:
    // send them back unchanged so a v2 write never drops them.
    const body = { ...values, policyIds: role?.policyIds ?? [] };
    if (mode === 'edit' && role) {
      await updateRole(role.id, body);
    } else {
      await createRole(body);
    }
    showNotification({
      type: 'success',
      message: mode === 'edit' ? 'Role updated' : 'Role created',
    });
    onClose();
  });

  const toOptions = (policies: readonly { id: string; name: string }[] = []) =>
    policies.map((policy) => ({ label: policy.name, value: policy.id }));

  return (
    <SidePanel
      title={TITLE_BY_MODE[mode]}
      closeOnBackdropClick={false}
      onClose={onClose}
      footer={
        <div className='flex gap-xs'>
          {!readOnly && (
            <Button
              className='flex-1'
              dataTestId='role-form-save'
              icon='fa-floppy-disk'
              loading={formState.isSubmitting}
              onClick={() => void onSubmit()}
              type='button'
              variant='primary'
            >
              Save
            </Button>
          )}
          <Button
            className='flex-1'
            dataTestId='role-form-close'
            onClick={onClose}
            type='button'
            variant='outline'
          >
            Close
          </Button>
        </div>
      }
    >
      <form onSubmit={onSubmit}>
        <Card className='flex flex-col gap p-sm'>
          <FormTextField
            control={control}
            dataTestId='role-form-name'
            disabled={readOnly}
            label='Role name'
            name='name'
            type='text'
          />
          <FormTextField
            control={control}
            dataTestId='role-form-description'
            disabled={readOnly}
            label='Description'
            name='description'
            type='text'
          />
          <FormMultiSelect
            control={control}
            dataTestId='role-form-endpoint-policies'
            disabled={readOnly}
            label='Endpoint policies'
            name='endpointPolicyIds'
            options={toOptions(endpointPoliciesQuery.data)}
            placeholder='Select policies'
          />
          <FormMultiSelect
            control={control}
            dataTestId='role-form-data-policies'
            disabled={readOnly}
            label='Data policies'
            name='dataPolicyIds'
            options={toOptions(dataPoliciesQuery.data)}
            placeholder='Select policies'
          />
        </Card>
      </form>
    </SidePanel>
  );
};
